import React from "react";
import { Clock, TrendingUp, ListChecks, Hammer } from "lucide-react";

const STORIES = [
  {
    name: "Courtney",
    result: "Saved nearly 2 hours a week on email replies",
    desc: "By improving how she prompted AI, Courtney turned messy email drafts into cleaner responses faster.",
    label: "Example win: Work Productivity",
    icon: Clock,
    accent: "#0E9F6E",
  },
  {
    name: "Marcus",
    result: "Built a cleaner outreach system",
    desc: "Better prompts helped him create follow-up messages, objection responses, and call notes for sales leads.",
    label: "Example win: Sales",
    icon: TrendingUp,
    accent: "#2563EB",
  },
  {
    name: "Jenna",
    result: "Turned repeat tasks into a workflow",
    desc: "She used AI to create checklists, SOPs, and reusable task templates for daily operations.",
    label: "Example win: Automation",
    icon: ListChecks,
    accent: "#E85D33",
  },
  {
    name: "Alex",
    result: "Mapped an app idea faster",
    desc: "Stronger prompts helped turn a rough idea into feature notes, user flows, and clearer build instructions.",
    label: "Example win: Build",
    icon: Hammer,
    accent: "#7C3AED",
  },
];

function StoryCard({ story, compact }) {
  const Icon = story.icon;
  return (
    <div
      data-testid={`impact-story-card-${story.name.toLowerCase()}`}
      className={`rounded-xl bg-white ${compact ? "p-4" : "p-4"}`}
      style={{ border: "1px solid var(--line)", boxShadow: "0 1px 2px rgba(16,16,16,0.04)" }}
    >
      <div className="flex items-start gap-3">
        <div
          className="shrink-0 rounded-lg flex items-center justify-center"
          style={{ width: 30, height: 30, background: `${story.accent}12`, color: story.accent }}
          aria-hidden
        >
          <Icon size={15} strokeWidth={2.2} />
        </div>
        <div className="min-w-0">
          <p className="text-[13px] font-semibold leading-snug" style={{ color: "var(--ink-1)" }}>
            {story.result}
          </p>
          <p className="mt-1.5 text-xs leading-relaxed" style={{ color: "var(--ink-3)" }}>
            <span className="font-medium" style={{ color: "var(--ink-2)" }}>{story.name}</span>
            {" — "}
            {story.desc}
          </p>
          <span
            className="mt-2 inline-block text-[10px] font-semibold uppercase tracking-[0.12em]"
            style={{ color: story.accent }}
          >
            {story.label}
          </span>
        </div>
      </div>
    </div>
  );
}

/** Desktop side rail — slim vertical column beside the hero / example card. */
export function ImpactStoriesRail() {
  return (
    <aside
      data-testid="impact-stories-rail"
      className="hidden lg:flex flex-col gap-3 w-full"
      aria-label="Real-World AI Wins"
    >
      <div className="text-[11px] font-bold uppercase tracking-[0.18em]" style={{ color: "var(--ink-3)" }}>
        Real-World AI Wins
      </div>
      {STORIES.map((s) => (
        <StoryCard key={s.name} story={s} />
      ))}
    </aside>
  );
}

/** Mobile — compact swipeable carousel below the hero CTA / example card. */
export function ImpactStoriesCarousel() {
  return (
    <section
      data-testid="impact-stories-carousel"
      className="lg:hidden mt-6 px-4 sm:px-6"
      aria-label="Real-World AI Wins"
    >
      <div className="text-[11px] font-bold uppercase tracking-[0.18em] mb-2.5" style={{ color: "var(--ink-3)" }}>
        Real-World AI Wins
      </div>
      <div
        className="flex gap-3 overflow-x-auto pb-2 -mx-4 px-4 sm:-mx-6 sm:px-6"
        style={{ scrollSnapType: "x mandatory", WebkitOverflowScrolling: "touch" }}
      >
        {STORIES.map((s) => (
          <div key={s.name} className="shrink-0 w-[82%] max-w-[300px]" style={{ scrollSnapAlign: "start" }}>
            <StoryCard story={s} compact />
          </div>
        ))}
      </div>
    </section>
  );
}
